import { Injectable } from '@angular/core';
import { ACCEPTANCE_LANGUAGE, DEFAULT_LANGUAGE } from '@core/constants';
import {
  PaymentBalacingPaymentStatus,
  PaymentBalacingSSE,
  PaymentMethodModel,
  PaymentsCardResponseModel,
  ThreeDSType,
} from '@core/models';
import { SessionState } from '@core/state/shared';
import { camelizeData } from '@core/utils';
import { environment } from '@env/environment';
import { __VERSION__ } from '@rcsa-frontend/__version__';
import { BlockUIService } from 'ng-block-ui';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { NotificationService } from '../notification/notification.service';

@Injectable({
  providedIn: 'root',
})
export class ThreeDSService {
  public static blockUiName = '3ds-loader-block-ui';
  public static errorFailed = '3DS:failed';
  public static errorTimeout = '3DS:timeout';
  public type$: BehaviorSubject<ThreeDSType> = new BehaviorSubject<ThreeDSType>(
    ThreeDSType.ADDING_CARD
  );
  private data: PaymentMethodModel | PaymentsCardResponseModel;
  private iframe: HTMLIFrameElement | null = null;
  private _solved = false;
  private sseConfiguration: { baseUrl: string; token: string; tries: number };
  private statusMethod: (
    paymentMethodId: string
  ) => Observable<PaymentMethodModel | PaymentsCardResponseModel>;
  private threeDSSolved$: Subject<
    PaymentMethodModel | PaymentsCardResponseModel
  >;

  constructor(
    private blockUiService: BlockUIService,
    private notificationService: NotificationService,
    private sessionState: SessionState
  ) {}

  public get solved(): boolean {
    return this._solved;
  }

  private set solved(value: boolean) {
    this._solved = value;
    if (value && this.iframe) {
      document.body.removeChild(this.iframe);
      this.iframe = null;
    }
  }

  public open3DS(
    type: ThreeDSType.VALIDATING_PAYMENT,
    data: PaymentsCardResponseModel,
    sse: { baseUrl: string; token: string },
    getStatusMethod: (
      paymentId: string
    ) => Observable<PaymentsCardResponseModel>
  ): Observable<PaymentsCardResponseModel>;
  public open3DS(
    type: ThreeDSType.ADDING_CARD | ThreeDSType.VALIDATING_CARD,
    data: PaymentMethodModel,
    sse: { baseUrl: string; token: string },
    getStatusMethod: (
      paymentMethodId: string
    ) => Observable<PaymentMethodModel | PaymentsCardResponseModel>
  ): Observable<PaymentMethodModel>;
  public open3DS(
    type: ThreeDSType,
    data: PaymentsCardResponseModel | PaymentMethodModel,
    sse: { baseUrl: string; token: string },
    getStatusMethod: (
      id: string
    ) => Observable<PaymentsCardResponseModel | PaymentMethodModel>
  ): Observable<PaymentsCardResponseModel | PaymentMethodModel> {
    this.type$.next(type);
    this.data = data;
    this.sseConfiguration = { ...sse, tries: 0 };
    this.statusMethod = getStatusMethod;
    this.threeDSSolved$ = new Subject<
      PaymentMethodModel | PaymentsCardResponseModel
    >();
    this.solved = false;
    this.threeDSSolved$.subscribe({
      next: () => {
        this.solved = true;
      },
      error: () => {
        this.solved = true;
      },
    });

    this.openIframe();
    this.observe3DSStatusBySSE();

    return this.threeDSSolved$.asObservable();
  }

  public startLoader(type: ThreeDSType) {
    this.type$.next(type);
    this.blockUiService.start(ThreeDSService.blockUiName);
  }

  private observe3DSStatusByEndpoint() {
    let tries = 0;
    const triesHandler = () => {
      tries++;
      if (tries > 200) {
        clearInterval(intervalKiller);
        this.notificationService.displayError(
          'No se pudo validar el método de pago.'
        );
        this.threeDSSolved$.error(ThreeDSService.errorTimeout);
        this.threeDSSolved$.complete();
      }
    };

    const intervalKiller = setInterval(() => {
      this.statusMethod(this.data.id).subscribe({
        next: (response: PaymentMethodModel | PaymentsCardResponseModel) => {
          if (this.type$.value === ThreeDSType.VALIDATING_PAYMENT) {
            const payment = response as PaymentsCardResponseModel;
            if (!this.solved) {
              if (payment.status.toLowerCase() === 'pagado') {
                this.threeDSSolved$.next(payment);
                this.threeDSSolved$.complete();
                this.notificationService.displayAlert(
                  'El pago fue realizado correctamente.',
                  'success'
                );
                clearInterval(intervalKiller);
              } else if (payment.status.toLowerCase() === 'rechazado') {
                this.threeDSSolved$.error(ThreeDSService.errorFailed);
                this.threeDSSolved$.complete();
                this.notificationService.displayError(
                  'La validación del método de pago falló.'
                );
                clearInterval(intervalKiller);
              } else {
                triesHandler();
              }
            }
          } else {
            const paymentMethod = response as PaymentMethodModel;
            if (paymentMethod.isValidated && !this.solved) {
              this.threeDSSolved$.next({ ...paymentMethod, isValidated: 1 });
              this.threeDSSolved$.complete();
              this.notificationService.displayAlert(
                'El método de pago fue agregado correctamente.',
                'success'
              );
              clearInterval(intervalKiller);
            } else {
              triesHandler();
            }
          }
        },
        error: (error) => {
          if (error.status === 404) {
            this.threeDSSolved$.error(ThreeDSService.errorFailed);
            this.threeDSSolved$.complete();
            this.notificationService.displayError(
              'La validación del método de pago falló.'
            );
            clearInterval(intervalKiller);
          } else {
            triesHandler();
          }
        },
      });
    }, 5000);
  }

  private observe3DSStatusBySSE() {
    const sseConfiguration = this.sseConfiguration!;
    sseConfiguration.tries++;
    if (sseConfiguration.tries <= 5) {
      const sse = new EventSourcePolyfill(
        `${environment.apiUrl}${sseConfiguration.baseUrl}`,
        {
          headers: {
            Authorization: `Bearer ${sseConfiguration.token}`,
            'X-App-Version': __VERSION__,
            'Accept-Language':
              ACCEPTANCE_LANGUAGE.get(this.sessionState.country) ||
              DEFAULT_LANGUAGE,
          },
        }
      );
      sse.onerror = (e: { error?: any; status?: number }) => {
        if (e.error || e.status) {
          sse.close();
          this.observe3DSStatusBySSE();
        }
      };
      sse.onmessage = (e: { data: string }) => {
        const event: PaymentBalacingSSE = camelizeData(JSON.parse(e.data));
        if (this.type$.value === ThreeDSType.VALIDATING_PAYMENT) {
          if (!this.solved) {
            if (event.payment.status === PaymentBalacingPaymentStatus.PAID) {
              this.threeDSSolved$.next({
                ...this.data,
                status: event.payment.status,
              });
              this.threeDSSolved$.complete();
              this.notificationService.displayAlert(
                'El pago fue realizado correctamente.',
                'success'
              );
              sse.close();
            } else if (
              event.payment.status === PaymentBalacingPaymentStatus.REJECTED
            ) {
              this.threeDSSolved$.error(ThreeDSService.errorFailed);
              this.threeDSSolved$.complete();
              this.notificationService.displayError(
                'La validación del método de pago falló.'
              );
              sse.close();
            }
          }
        } else {
          const paymentMethod = camelizeData(JSON.parse(e.data))
            .paymentMethod as PaymentMethodModel;
          if (paymentMethod.isValidated && !this.solved) {
            this.threeDSSolved$.next({ ...paymentMethod, isValidated: 1 });
            this.threeDSSolved$.complete();
            this.notificationService.displayAlert(
              'El método de pago fue agregado correctamente.',
              'success'
            );
            sse.close();
          }
          if (!paymentMethod.isActive) {
            this.threeDSSolved$.error(ThreeDSService.errorFailed);
            this.threeDSSolved$.complete();
            this.notificationService.displayError(
              'La validación del método de pago falló.'
            );
            sse.close();
          }
        }
      };
    } else {
      this.observe3DSStatusByEndpoint();
    }
  }

  private openIframe() {
    const iframeContent = atob(this.data.iframe as string);
    this.iframe = document.createElement('iframe');
    this.iframe.srcdoc = iframeContent;
    this.iframe.style.width = 'calc(100vw - 80px)';
    this.iframe.style.height = 'calc(100vh - 200px)';
    this.iframe.style.border = 'none';
    this.iframe.style.position = 'fixed';
    this.iframe.style.top = '100px';
    this.iframe.style.left = '40px';
    this.iframe.style.zIndex = '99999';
    this.iframe.style.backgroundColor = 'white';
    document.body.appendChild(this.iframe);
  }
}
